<template>
  <header class="nav" id="top">
    <div class="wrap">
      <a class="brand" href="#top" aria-label="Adverpost home" @click.prevent="toTop">
        <landing-logo class="logo" />
      </a>
      <nav class="nav-links" aria-label="Main">
        <a v-for="l in links" :key="l.href" :href="l.href" @click.prevent="go(l.href)">{{ l.label }}</a>
        <a href="https://support.adverpost.com/" target="_blank" rel="noopener">Support</a>
      </nav>
      <theme-toggle />
      <div class="nav-cta">
        <a class="btn btn-line btn-sm" href="#/login" data-auth="login">Log in</a>
        <a class="btn btn-red btn-sm" href="#/home" data-auth="register">Get Adverpost</a>
      </div>
      <a class="btn btn-red btn-sm nav-mob" href="#/home" data-auth="register">Get Adverpost</a>
      <button
        ref="burger"
        class="burger"
        type="button"
        :aria-expanded="String(open)"
        aria-controls="drawer"
        :aria-label="open ? 'Close menu' : 'Open menu'"
        @click="setOpen(!open)"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
          <path v-if="open" d="M6 6l12 12M18 6L6 18" />
          <path v-else d="M4 7h16M4 12h16M4 17h16" />
        </svg>
      </button>
    </div>
    <!-- On phones the menu covers the page (see _phone.scss): links first, the
         account buttons at the bottom. -->
    <div id="drawer" ref="drawer" class="drawer" :hidden="!open" tabindex="-1" aria-label="Menu" @click="onDrawerClick">
      <div class="wrap">
        <a v-for="l in links" :key="l.href" class="link" :href="l.href" @click.prevent="go(l.href)">{{ l.label }}</a>
        <a class="link" href="https://support.adverpost.com/" target="_blank" rel="noopener">Support</a>
        <a class="link" href="#demo" data-demo>Book a demo</a>
        <div class="cta-row">
          <a class="btn btn-red" href="#/home" data-auth="register">Create account</a>
          <a class="btn btn-line" href="#/login" data-auth="login">Log in</a>
        </div>
      </div>
    </div>
  </header>
</template>

<script>
import LandingLogo from "./LandingLogo.vue";
import ThemeToggle from "./ThemeToggle.vue";

// Set on <html> while the menu is open, so the page behind it stays still.
const MENU_OPEN_CLASS = "landing-menu-open";

export default {
  name: "LandingNav",
  components: { LandingLogo, ThemeToggle },
  data() {
    return {
      open: false,
      links: [
        { href: "#platform", label: "Platform" },
        { href: "#adverpeople", label: "Adverpeople" },
        { href: "#marketplace", label: "Marketplace" },
        { href: "#pricing", label: "Pricing" },
        { href: "#operators", label: "Operators" },
      ],
    };
  },
  mounted() {
    window.addEventListener("resize", this.onResize);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.onResize);
    document.removeEventListener("keydown", this.onKey);
    document.documentElement.classList.remove(MENU_OPEN_CLASS);
  },
  methods: {
    // Opening locks the page and moves focus into the menu; Escape closes it
    // and returns focus to the menu button. The lock is set straight away, not
    // from a watcher, so a menu link can scroll the page right after closing.
    setOpen(open) {
      this.open = open;
      document.documentElement.classList.toggle(MENU_OPEN_CLASS, open);
      if (open) {
        document.addEventListener("keydown", this.onKey);
        // Focus the sheet itself, not its first link, so screen readers start
        // inside the menu without a focus ring appearing on touch screens.
        this.$nextTick(() => this.$refs.drawer.focus());
      } else {
        document.removeEventListener("keydown", this.onKey);
      }
    },
    onKey(e) {
      if (e.key !== "Escape") return;
      this.setOpen(false);
      this.$refs.burger.focus();
    },
    onResize() {
      if (this.open && window.innerWidth > 960) this.setOpen(false);
    },
    // The app runs on a hash router, so in-page #anchors cannot be plain hrefs:
    // the router would treat them as routes. Scroll to the target instead.
    go(href) {
      this.setOpen(false);
      // getElementById takes the id as a value, so nothing is parsed as a
      // selector. Interpolating into querySelector would break, or match
      // something unintended, on an id containing quotes or brackets.
      const target = document.getElementById(href.slice(1));
      if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
    },
    // Any link in the open menu closes it, including the sign-up, log-in and
    // contact links, which LandingPage handles through their data-* attributes.
    onDrawerClick(e) {
      if (e.target.closest("a")) this.setOpen(false);
    },
    toTop() {
      this.setOpen(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
  },
};
</script>
